import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { SWRConfig } from 'swr';
import { ThemeProvider } from './contexts/ThemeContext';
import { TableUiThemeProvider } from './contexts/TableUiThemeContext';
import { UiLayoutPreferencesProvider } from './contexts/UiLayoutPreferencesContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import UiPreferencesBridge from './components/UiPreferencesBridge';
import Login from './components/Login';
import MainLayout from './components/MainLayout';
import Dashboard from './dashboard/Dashboard';
import DocumentsPage from './modules/document-management/document/documentPage';
import ProceduresPage from './modules/procedure-management/procedure/procedurePage';
import MyProceduresPage from './modules/procedure-management/MyProceduresPage';
import LoadingSpinner from './components/LoadingSpinner';
import DocumentStatusPage from './modules/document-management/documentStatus/documentStatusPage';
import DocumentTypePage from './modules/document-management/documentType/documentTypePage';
import DocumentModulePage from './modules/document-management/documentModule/documentModulePage';
import DepartmentPage from './admin/department/departmentPage';
import UserDepartmentPage from './admin/userDepartment/userDepartmentPage';
import UsersPage from './admin/users/usersPage';
import RolePage from './admin/role/rolePage';
import UserPermissionsPage from './admin/permissions/UserPermissionsPage';
import EntityTypePage from './admin/entityType/entityTypePage';
import PriorityPage from './admin/priority/priorityPage';
import RiskCategoryPage from './admin/config/riskCategory/riskCategoryPage';
import CapaTypePage from './admin/config/capaType/capaTypePage';
import CapaSourcePage from './admin/config/capaSource/capaSourcePage';
import CapaStatusPage from './admin/config/capaStatus/capaStatusPage';
import CapaActionStatusPage from './admin/config/capaActionStatus/capaActionStatusPage';
import RiskStatusPage from './admin/config/riskStatus/riskStatusPage';
import NonConformityReportStatusPage from './admin/config/nonConformityReportStatus/nonConformityReportStatusPage';
import NonConformityTypePage from './admin/config/nonConformityType/nonConformityTypePage';
import ChangeStatusPage from './admin/config/changeStatus/changeStatusPage';
import ChangeTypePage from './admin/config/changeType/changeTypePage';
import ChangeCategoryPage from './admin/config/changeCategory/changeCategoryPage';
import TrainingStatusPage from './admin/config/trainingStatus/trainingStatusPage';
import ComplaintActionTypePage from './admin/config/complaintActionType/complaintActionTypePage';
import ComplaintActionTakenTypePage from './admin/config/complaintActionTakenType/complaintActionTakenTypePage';
import ComplaintTypePage from './admin/config/complaintType/complaintTypePage';
import ComplaintStatusPage from './admin/config/complaintStatus/complaintStatusPage';
import ComplaintSourcePage from './admin/config/complaintSource/complaintSourcePage';
import ComplaintCategoryNonMedicalPage from './admin/config/complaintCategoryNonMedical/complaintCategoryNonMedicalPage';
import ComplaintSampleAvailablePage from './admin/config/complaintSampleAvailable/complaintSampleAvailablePage';
import ComplaintSampleInspectionPage from './admin/config/complaintSampleInspection/complaintSampleInspectionPage';
import ComplaintForwardToPage from './admin/config/complaintForwardTo/complaintForwardToPage';
import ComplaintFindingTypePage from './admin/config/complaintFindingType/complaintFindingTypePage';
import ComplaintInvestigationCategoryPage from './admin/config/complaintInvestigationCategory/complaintInvestigationCategoryPage';
import CapaPage from './modules/capa/capaPage';

// Import main business entities - Re-enabling one by one to debug
import NonConformityReportPage from './modules/non-conformity-report/nonConformityReportPage';
import RiskAssessmentPage from './modules/risk-assessment/riskAssessmentPage';
import ComplaintPage from './modules/complaint/complaintPage';
import DeviationPage from './modules/deviation/deviationPage';
import DeviationPrintPage from './modules/deviation/DeviationPrintPage';
import ExternalAuditPage from './modules/external-audit/externalAuditPage';
import ReturnedProductInspectionPage from './modules/returned-product-inspection/returnedProductInspectionPage';
import ReturnedProductInspectionPrintPage from './modules/returned-product-inspection/ReturnedProductInspectionPrintPage';
import ChangeRequestPage from './modules/change-request/changeRequestPage';
import CapaEffectivenessPage from './modules/capa-effectiveness/capaEffectivenessPage';
import CapaEffectivenessPrintPage from './modules/capa-effectiveness/CapaEffectivenessPrintPage';
import ProductStatusChangePrintPage from './modules/product-status-change/ProductStatusChangePrintPage';
import DatabaseBrowserPage from './modules/database-browser/databaseBrowserPage';
import DatabaseMigrationsPage from './modules/database-migrations/databaseMigrationsPage';
import RiskAssessmentPrintPage from './modules/risk-assessment/RiskAssessmentPrintPage';
import TrainingAssignmentPage from './modules/training-assignment/trainingAssignmentPage';
import EmployeePage from './modules/training-management/employee/employeePage';
import TrainingPage from './modules/training-management/training/trainingPage';
import SupplierPage from './modules/supplier/supplierPage';
import SupplierRatingPage from './modules/supplier-rating/supplierRatingPage';
import ContractPage from './modules/contracts/contractPage';
import ContractCategoryPage from './modules/contracts/contractCategoryPage';
import ContractDashboardPage from './modules/contracts/contractDashboardPage';
import ManualPage from './modules/manuals/manualPage';
import ManualTypePage from './modules/manuals/manualTypePage';
import MyManualsPage from './modules/manuals/MyManualsPage';
import DepositorPage from './modules/customer/depositorPage';
import DistributorPage from './modules/distributor/distributorPage';
import TodoPage from './modules/todo/todoPage';
import TodoCalendarPage from './modules/todo/TodoCalendarPage';
import ApplicationSettingPage from './modules/application-setting/applicationSettingPage';
import ReferenceDocumentsPage from './modules/reference-documents/referenceDocumentsPage';
import { UnderConstruction } from './components';
import ReportsPage from './modules/reports/ReportsPage';
import RiskAssessmentAnalyticsReport from './modules/reports/risk-assessment/RiskAssessmentAnalyticsReport';
import ComplaintAnalyticsReport from './modules/reports/complaint/ComplaintAnalyticsReport';
import TicketingAnalyticsReport from './modules/reports/ticketing/TicketingAnalyticsReport';
import TicketingStatusAnalyticsReport from './modules/report-schedule/reports/TicketingStatusAnalyticsReport';
import OpenTodosReport from './modules/report-schedule/reports/OpenTodosReport';
import DeviationAnalyticsReport from './modules/reports/deviation/DeviationAnalyticsReport';
import ProductStatusChangeAnalyticsReport from './modules/reports/product-status-change/ProductStatusChangeAnalyticsReport';
import TodoAnalyticsReport from './modules/reports/todo/TodoAnalyticsReport';
import ManualAnalyticsReport from './modules/reports/manual/ManualAnalyticsReport';
import ContractAnalyticsReport from './modules/reports/contract/ContractAnalyticsReport';
import MyApprovalsPage from './modules/approval/my-approvals/myApprovalsPage';
import ApprovalRequestsPage from './modules/approval/approval-requests/approvalRequestsPage';
import ApprovalTemplatesPage from './modules/approval/approval-templates/approvalTemplatesPage';
import AnalyticsPage from './modules/approval/analytics/analyticsPage';
import EmailTemplatePage from './modules/email-template/EmailTemplatePage';
import ReportSchedulePage from './modules/report-schedule/ReportSchedulePage';
import ProductStatusChangePage from './modules/product-status-change/productStatusChangePage';
import TicketingHrPage from './modules/ticketing/hr/TicketingHrPage';
import TicketingDepartmentPage from './modules/ticketing/TicketingDepartmentPage';
import TicketingRequestPage from './modules/ticketing/TicketingRequestPage';
import TicketingItQueuePage from './modules/ticketing/TicketingItQueuePage';
import TicketingTicketDetailPage from './modules/ticketing/TicketingTicketDetailPage';
import TicketingDashboardPage from './modules/ticketing/TicketingDashboardPage';
import { fetchTicketingDepartments } from './modules/ticketing/ticketingApi';
import {
  getPostLoginRedirectPath,
  isSafePostLoginReturnPath,
} from './admin/permissions/permissionStore';
import HiddenMenuRouteGuard from './components/HiddenMenuRouteGuard';
import { useHiddenMenuHrefs } from './hooks/useApplicationSetting';

/** After login: first route the user may open (same rules as the sidebar), e.g. ticketing-only → HR department. */
const PostLoginNavigate = () => {
  const { user } = useAuth();
  const { hiddenHrefs } = useHiddenMenuHrefs();
  const [path, setPath] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const depts = await fetchTicketingDepartments();
        if (!cancelled) {
          setPath(getPostLoginRedirectPath(user, depts, user?.role?.name, hiddenHrefs));
        }
      } catch {
        if (!cancelled) {
          setPath(getPostLoginRedirectPath(user, [], user?.role?.name, hiddenHrefs));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, hiddenHrefs]);

  if (path == null) {
    return <LoadingSpinner />;
  }
  return <Navigate to={path} replace />;
};

// Protected Route Component
const ProtectedRoute = ({ children, pageTitle }) => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    // Only remember where we were for allowlisted paths (e.g. email links to /ticketing/ticket/…).
    // Other routes (e.g. /users) are not passed to /login so the next login is not sent there by mistake.
    const loginState = isSafePostLoginReturnPath(location.pathname)
      ? { from: location }
      : undefined;
    return <Navigate to="/login" replace state={loginState} />;
  }

  const userRole = user?.role?.name || 'user';

  return (
    <MainLayout onLogout={logout} userRole={userRole} pageTitle={pageTitle}>
      <HiddenMenuRouteGuard>{children}</HiddenMenuRouteGuard>
    </MainLayout>
  );
};

// Print Route Component - Bypasses MainLayout for clean printing
const PrintRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Return without MainLayout wrapper for clean printing
  return <>{children}</>;
};

// Login Route Component
const LoginRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    const from = location.state?.from;
    const path = from?.pathname;
    if (path && isSafePostLoginReturnPath(path)) {
      const suffix = from.search || '';
      const hash = from.hash || '';
      return <Navigate to={`${path}${suffix}${hash}`} replace />;
    }
    return <PostLoginNavigate />;
  }

  return <Login />;
};

function AppContent() {

  return (
    <Router 
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <div className="App">
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route 
            path="/" 
            element={
              <ProtectedRoute pageTitle="Quality Management Dashboard">
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/documents" 
            element={
              <ProtectedRoute pageTitle="Documents">
                <DocumentsPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/procedures"
            element={
              <ProtectedRoute pageTitle="Procedures">
                <ProceduresPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-procedures"
            element={
              <ProtectedRoute pageTitle="My Procedures">
                <MyProceduresPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/admin/document-types" 
            element={
              <ProtectedRoute pageTitle="Document Types">
                <DocumentTypePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/document-status" 
            element={
              <ProtectedRoute pageTitle="Document Status">
                <DocumentStatusPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/document-modules" 
            element={
              <ProtectedRoute pageTitle="Document Modules">
                <DocumentModulePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/departments" 
            element={
              <ProtectedRoute pageTitle="Department Management">
                <DepartmentPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/admin/user-departments"
            element={
              <ProtectedRoute pageTitle="User Department Management">
                <UserDepartmentPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/users" 
            element={
              <ProtectedRoute pageTitle="User Management">
                <UsersPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/roles" 
            element={
              <ProtectedRoute pageTitle="Role Management">
                <RolePage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/admin/user-permissions"
            element={
              <ProtectedRoute pageTitle="User Permissions">
                <UserPermissionsPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/admin/entity-types" 
            element={
              <ProtectedRoute pageTitle="Entity Type">
                <EntityTypePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/priorities" 
            element={
              <ProtectedRoute pageTitle="Priority">
                <PriorityPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/risk-categories" 
            element={
              <ProtectedRoute pageTitle="Risk Category">
                <RiskCategoryPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/risk-status" 
            element={
              <ProtectedRoute pageTitle="Risk Status">
                <RiskStatusPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/capa-types" 
            element={
              <ProtectedRoute pageTitle="CAPA Type">
                <CapaTypePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/capa-sources" 
            element={
              <ProtectedRoute pageTitle="CAPA Source">
                <CapaSourcePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/capa-status" 
            element={
              <ProtectedRoute pageTitle="CAPA Status">
                <CapaStatusPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/ncr-status" 
            element={
              <ProtectedRoute pageTitle="NCR Status">
                <NonConformityReportStatusPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/ncr-types" 
            element={
              <ProtectedRoute pageTitle="NCR Type">
                <NonConformityTypePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/change-status" 
            element={
              <ProtectedRoute pageTitle="Change Status">
                <ChangeStatusPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/change-types" 
            element={
              <ProtectedRoute pageTitle="Change Type">
                <ChangeTypePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/change-categories" 
            element={
              <ProtectedRoute pageTitle="Change Category">
                <ChangeCategoryPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/training-status" 
            element={
              <ProtectedRoute pageTitle="Training Status">
                <TrainingStatusPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/complaint-action-types" 
            element={
              <ProtectedRoute pageTitle="Complaint Action Type">
                <ComplaintActionTypePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/complaint-action-taken-types" 
            element={
              <ProtectedRoute pageTitle="Complaint Action Taken Type">
                <ComplaintActionTakenTypePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/complaint-types" 
            element={
              <ProtectedRoute pageTitle="Complaint Type">
                <ComplaintTypePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/complaint-status" 
            element={
              <ProtectedRoute pageTitle="Complaint Status">
                <ComplaintStatusPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/complaint-sources" 
            element={
              <ProtectedRoute pageTitle="Complaint Source">
                <ComplaintSourcePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/complaint-category-non-medical" 
            element={
              <ProtectedRoute pageTitle="Complaint Category (Non-Medical)">
                <ComplaintCategoryNonMedicalPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/complaint-sample-available" 
            element={
              <ProtectedRoute pageTitle="Complaint Sample Available">
                <ComplaintSampleAvailablePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/complaint-sample-inspections" 
            element={
              <ProtectedRoute pageTitle="Complaint Sample Inspections">
                <ComplaintSampleInspectionPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/complaint-forward-to" 
            element={
              <ProtectedRoute pageTitle="Complaint Forward To">
                <ComplaintForwardToPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/complaint-finding-type" 
            element={
              <ProtectedRoute pageTitle="Complaint Finding Type">
                <ComplaintFindingTypePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/complaint-investigation-category" 
            element={
              <ProtectedRoute pageTitle="Complaint Investigation Category">
                <ComplaintInvestigationCategoryPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/capa-action-status" 
            element={
              <ProtectedRoute pageTitle="CAPA Action Status">
                <CapaActionStatusPage />
              </ProtectedRoute>
            } 
          />

          {/* Main Business Entity Routes - Re-enabling one by one to debug */}
          <Route 
            path="/capa" 
            element={
              <ProtectedRoute pageTitle="CAPA">
                <CapaPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ncr" 
            element={
              <ProtectedRoute pageTitle="Non-Conformities">
                <NonConformityReportPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/risk-assessment" 
            element={
              <ProtectedRoute pageTitle="Risk Assessments">
                <RiskAssessmentPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/approval" 
            element={<Navigate to="/approval/approval-requests" replace />}
          />
          <Route 
            path="/approval/my-approvals" 
            element={
              <ProtectedRoute pageTitle="My Approvals">
                <MyApprovalsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/approval/approval-requests" 
            element={
              <ProtectedRoute pageTitle="Approval Requests">
                <ApprovalRequestsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/approval/templates" 
            element={
              <ProtectedRoute pageTitle="Approval Templates">
                <ApprovalTemplatesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/approval/analytics" 
            element={
              <ProtectedRoute pageTitle="Approval Analytics">
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/complaint" 
            element={
              <ProtectedRoute pageTitle="Complaints">
                <ComplaintPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/product-status-change" 
            element={
              <ProtectedRoute pageTitle="Product Status Change">
                <ProductStatusChangePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/product-status-change/:id/print" 
            element={
              <PrintRoute>
                <ProductStatusChangePrintPage />
              </PrintRoute>
            } 
          />
          <Route 
            path="/deviation/:id/print" 
            element={
              <PrintRoute>
                <DeviationPrintPage />
              </PrintRoute>
            } 
          />
          <Route
            path="/capa-effectiveness-check/:id/print"
            element={
              <PrintRoute>
                <CapaEffectivenessPrintPage />
              </PrintRoute>
            }
          />
          <Route 
            path="/risk-assessment/:id/print" 
            element={
              <PrintRoute>
                <RiskAssessmentPrintPage />
              </PrintRoute>
            } 
          />
          <Route 
            path="/training-management/employee" 
            element={
              <ProtectedRoute pageTitle="Employees">
                <EmployeePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/training-management/training" 
            element={
              <ProtectedRoute pageTitle="Trainings">
                <TrainingPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/training-assignment" 
            element={
              <ProtectedRoute pageTitle="Training Assignments">
                <TrainingAssignmentPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/supplier" 
            element={
              <ProtectedRoute pageTitle="Suppliers">
                <SupplierPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/supplier-rating" 
            element={
              <ProtectedRoute pageTitle="Supplier Ratings">
                <SupplierRatingPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/contract-dashboard"
            element={
              <ProtectedRoute pageTitle="Contract Dashboard">
                <ContractDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contract"
            element={
              <ProtectedRoute pageTitle="Contracts">
                <ContractPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contract-category"
            element={
              <ProtectedRoute pageTitle="Contract Categories">
                <ContractCategoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manual"
            element={
              <ProtectedRoute pageTitle="Manuals">
                <ManualPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manual-type"
            element={
              <ProtectedRoute pageTitle="Manual Types">
                <ManualTypePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-manuals"
            element={
              <ProtectedRoute pageTitle="My Manuals">
                <MyManualsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/depositor"
            element={
              <ProtectedRoute pageTitle="Depositors">
                <DepositorPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/distributor" 
            element={
              <ProtectedRoute pageTitle="Distributors">
                <DistributorPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/todo" 
            element={
              <ProtectedRoute pageTitle="Todo's">
                <TodoPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/todo/calendar"
            element={
              <ProtectedRoute pageTitle="Todo's Calendar">
                <TodoCalendarPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/application-settings" 
            element={
              <ProtectedRoute pageTitle="Application Settings">
                <ApplicationSettingPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/reference-documents" 
            element={
              <ProtectedRoute pageTitle="Reference Documents">
                <ReferenceDocumentsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/email-templates" 
            element={
              <ProtectedRoute pageTitle="Email Templates">
                <EmailTemplatePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute pageTitle="Settings">
                <UnderConstruction 
                  title="Settings"
                  subtitle="System configuration and preferences"
                  description="We're building comprehensive system settings with user preferences, application configuration, and advanced customization options."
                  progress={5}
                  estimatedCompletion="Q3 2025"
                />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/database-browser"
            element={
              <ProtectedRoute pageTitle="Database Browser">
                <DatabaseBrowserPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/database-migrations"
            element={
              <ProtectedRoute pageTitle="Database Migrations">
                <DatabaseMigrationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deviation"
            element={
              <ProtectedRoute pageTitle="Deviations">
                <DeviationPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/capa-effectiveness-check"
            element={
              <ProtectedRoute pageTitle="CAPA Effectiveness Check">
                <CapaEffectivenessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/external-audit"
            element={
              <ProtectedRoute pageTitle="CAPA Plan">
                <ExternalAuditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/returned-product-inspection"
            element={
              <ProtectedRoute pageTitle="Returned Product Inspection">
                <ReturnedProductInspectionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/returned-product-inspection/:id/print"
            element={
              <PrintRoute>
                <ReturnedProductInspectionPrintPage />
              </PrintRoute>
            }
          />
          <Route
            path="/change-request"
            element={
              <ProtectedRoute pageTitle="Change Request">
                <ChangeRequestPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ticketing/dashboard"
            element={
              <ProtectedRoute pageTitle="Ticketing Dashboard">
                <TicketingDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ticketing/ticket/:ticketId"
            element={
              <ProtectedRoute pageTitle="Ticket">
                <TicketingTicketDetailPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/ticketing/department/:departmentId" 
            element={
              <ProtectedRoute pageTitle="Ticketing">
                <TicketingDepartmentPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ticketing/request/:departmentId/:categoryId" 
            element={
              <ProtectedRoute pageTitle="Ticket request">
                <TicketingRequestPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ticketing/options" 
            element={
              <ProtectedRoute pageTitle="Ticketing setup">
                <TicketingHrPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/ticketing/it/queue"
            element={
              <ProtectedRoute pageTitle="All tickets (IT)">
                <TicketingItQueuePage />
              </ProtectedRoute>
            }
          />
          <Route path="/ticketing/hr" element={<Navigate to="/ticketing/options" replace />} />

          {/* Reports & Analytics (year-scoped module reports) */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute pageTitle="Reports & Analytics">
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/complaint"
            element={
              <ProtectedRoute pageTitle="Complaint Analytics">
                <ComplaintAnalyticsReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/risk-assessment"
            element={
              <ProtectedRoute pageTitle="Risk Assessment Analytics">
                <RiskAssessmentAnalyticsReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/deviation"
            element={
              <ProtectedRoute pageTitle="Deviation Analytics">
                <DeviationAnalyticsReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/product-status-change"
            element={
              <ProtectedRoute pageTitle="Product Status Change Analytics">
                <ProductStatusChangeAnalyticsReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/todo"
            element={
              <ProtectedRoute pageTitle="Todos Analytics">
                <TodoAnalyticsReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/ticketing"
            element={
              <ProtectedRoute pageTitle="Ticketing Analytics">
                <TicketingAnalyticsReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/manuals"
            element={
              <ProtectedRoute pageTitle="Manuals Analytics">
                <ManualAnalyticsReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/contracts"
            element={
              <ProtectedRoute pageTitle="Contracts Analytics">
                <ContractAnalyticsReport />
              </ProtectedRoute>
            }
          />
          {/* Legacy report paths → hub */}
          <Route path="/reports/dashboard" element={<Navigate to="/reports" replace />} />
          <Route path="/reports/compliance" element={<Navigate to="/reports" replace />} />
          <Route path="/reports/quality-metrics" element={<Navigate to="/reports" replace />} />
          <Route path="/reports/training" element={<Navigate to="/reports" replace />} />
          <Route path="/reports/capa" element={<Navigate to="/reports" replace />} />
          <Route path="/reports/non-conformity" element={<Navigate to="/reports" replace />} />
          <Route path="/reports/supplier" element={<Navigate to="/reports" replace />} />
          <Route path="/reports/document" element={<Navigate to="/reports" replace />} />
          <Route path="/reports/external-audit" element={<Navigate to="/reports" replace />} />
          <Route
            path="/reports/schedules"
            element={
              <ProtectedRoute pageTitle="Report Schedules">
                <ReportSchedulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-scheduling/ticketing-status-analytics"
            element={
              <ProtectedRoute pageTitle="Ticketing Status and Analytics Report">
                <TicketingStatusAnalyticsReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-scheduling/open-todos"
            element={
              <ProtectedRoute pageTitle="Open Todos Report">
                <OpenTodosReport />
              </ProtectedRoute>
            }
          />

        </Routes>
      </div>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <TableUiThemeProvider>
        <UiLayoutPreferencesProvider>
          <SWRConfig value={{}}>
            <AuthProvider>
              <UiPreferencesBridge />
              <AppContent />
            </AuthProvider>
          </SWRConfig>
        </UiLayoutPreferencesProvider>
      </TableUiThemeProvider>
    </ThemeProvider>
  );
}

export default App;